import Phaser from "phaser";
import type UIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";
import { THEME, TEXT_STYLE, domainAccent } from "./theme";
import {
  formatRange, statMod, describeApplyStatus, describeEnhancement,
  type CardDef, type CardEffect, type DiceNotation, type DomainId, type Stat, type Stats,
} from "../../core/roscha";

interface RexScene extends Phaser.Scene {
  rexUI: UIPlugin;
}

const STAT_SHORT: Record<Stat, string> = {
  strength: "СИЛ",
  dexterity: "ЛОВ",
  intelligence: "ИНТ",
  wisdom: "МУД",
  constitution: "ТЕЛ",
};

const KIND_LABEL: Record<CardDef["kind"], string> = {
  attack: "АТАКА",
  spell: "ЗАКЛИНАНИЕ",
  action: "ДЕЙСТВИЕ",
};

const DEFAULT_CARD_W = 144;
const DEFAULT_CARD_H = 208;

function chamferedPoints(w: number, h: number, ct: number, cb: number): number[] {
  return [
    ct,     0,
    w - ct, 0,
    w,      ct,
    w,      h - cb,
    w - cb, h,
    cb,     h,
    0,      h - cb,
    0,      ct,
  ];
}

function chamferedTopOnlyPoints(w: number, h: number, ct: number): number[] {
  return [
    ct,     0,
    w - ct, 0,
    w,      ct,
    w,      h,
    0,      h,
    0,      ct,
  ];
}

interface BattleCardConfig {
  scene: Phaser.Scene;
  card: CardDef;
  canHope?: boolean;
  canFear?: boolean;
  interactive?: boolean;
  onPlayHope?: () => void;
  onPlayFear?: () => void;
  width?: number;
  height?: number;
  count?: number;
  stats?: Stats;
  level?: number;
  domainId?: DomainId;
}

export class BattleCard extends Phaser.GameObjects.Container {
  readonly cardWidth: number;
  readonly cardHeight: number;
  private bg: Phaser.GameObjects.Polygon;
  private baseY = 0;
  private baseDepth = 0;
  private isHovering = false;
  private hopeZone?: Phaser.GameObjects.Rectangle;
  private fearZone?: Phaser.GameObjects.Rectangle;
  private zoneSplitY = 0;
  private headerEndY = 0;
  private zoneBottomY = 0;
  private canHope = false;
  private canFear = false;
  private onPlayHope?: () => void;
  private onPlayFear?: () => void;
  private glowRing?: Phaser.GameObjects.Polygon;
  private ownerLevel?: number;
  public handIndex = 0;

  constructor(cfg: BattleCardConfig) {
    super(cfg.scene, 0, 0);
    this.ownerLevel = cfg.level;
    const { card } = cfg;
    const w = cfg.width ?? DEFAULT_CARD_W;
    const h = cfg.height ?? DEFAULT_CARD_H;
    const interactive = cfg.interactive ?? true;
    const canHope = cfg.canHope ?? true;
    const canFear = cfg.canFear ?? true;
    const playable = interactive && (canHope || canFear);
    this.cardWidth = w;
    this.cardHeight = h;
    const rex = (cfg.scene as RexScene).rexUI;
    const hopeEnhText = cfg.domainId ? describeEnhancement(card, cfg.domainId, "hope") : "";
    const fearEnhText = cfg.domainId ? describeEnhancement(card, cfg.domainId, "fear") : "";
    const enhColor = cfg.domainId ? `#${domainAccent(cfg.domainId).toString(16).padStart(6, "0")}` : "#c8a857";

    const ct = 16;
    const cb = 10;
    const kindColor = card.kind === "attack" ? THEME.kindAttack : card.kind === "spell" ? THEME.kindSpell : THEME.kindAction;

    if (playable) {
      const ringW = w + 22;
      const ringH = h + 22;
      this.glowRing = cfg.scene.add.polygon(0, 0, chamferedPoints(ringW, ringH, ct + 6, cb + 6), THEME.leafGlow, 1)
        .setOrigin(0.5, 0.5)
        .setAlpha(0);
      this.add(this.glowRing);
    }

    this.bg = cfg.scene.add.polygon(0, 0, chamferedPoints(w, h, ct, cb), THEME.bgPanel, interactive && !playable ? 0.5 : 0.96)
      .setOrigin(0.5, 0.5);
    this.bg.setStrokeStyle(playable ? 3 : 1, playable ? THEME.borderAccent : THEME.borderSubtle, playable ? 0.9 : 0.4);
    this.add(this.bg);
    if (playable) {
      this.bg.postFX?.addShadow(0, 2, 0.05, 0.5, 0x000000, 6, 0.85);
    }

    const innerY = -h / 2;
    const headerH = 60;
    const headerW = w - 8;
    const headerBg = cfg.scene.add.polygon(0, 0, chamferedTopOnlyPoints(headerW, headerH, ct - 2), kindColor, playable ? 0.95 : 0.4)
      .setOrigin(0.5, 0.5);
    headerBg.setPosition(0, innerY + headerH / 2 + 4);
    this.add(headerBg);

    const name = cfg.scene.add.text(0, innerY + 14, card.name, {
      ...TEXT_STYLE.display,
      fontSize: "15px",
      fontStyle: "bold",
      color: playable || !interactive ? "#fffbe8" : "#7a8074",
      wordWrap: { width: w - 18 },
      align: "center",
    }).setOrigin(0.5, 0);
    name.setLetterSpacing(1.3);
    name.setShadow(0, 1, "#000000", 2, false, true);
    this.add(name);

    const kindLine = `${KIND_LABEL[card.kind]}`;
    const kind = cfg.scene.add.text(0, innerY + headerH - 8, kindLine, {
      ...TEXT_STYLE.small,
      fontSize: "9px",
      fontStyle: "bold",
      color: playable || !interactive ? "#fffbe8" : "#4a4555",
    }).setOrigin(0.5, 0);
    kind.setLetterSpacing(2);
    this.add(kind);

    const sepY = innerY + headerH + 8;
    const sep = cfg.scene.add.rectangle(0, sepY, w - 22, 2, THEME.borderAccent, 0.7);
    this.add(sep);

    const costY = h / 2 - 16;
    const bodyTop = sepY + 6;
    const bodyBottom = costY - 14;
    const bodyMid = (bodyTop + bodyBottom) / 2;

    const hopeLabel = cfg.scene.add.text(0, bodyTop, "НАДЕЖДА", {
      ...TEXT_STYLE.small, fontSize: "9px", fontStyle: "bold", color: "#a8c896",
    }).setOrigin(0.5, 0);
    hopeLabel.setLetterSpacing(2);
    this.add(hopeLabel);

    const hopeFormula = this.renderFormulaText(cfg.scene, card.hope.effects, w, cfg.stats);
    hopeFormula.setPosition(0, bodyTop + 16);
    hopeFormula.setOrigin(0.5, 0);
    this.add(hopeFormula);

    if (hopeEnhText) {
      const hopeEnh = cfg.scene.add.text(0, bodyTop + 16 + hopeFormula.height + 2, hopeEnhText, {
        ...TEXT_STYLE.small,
        fontSize: "8.5px",
        fontStyle: "bold",
        color: enhColor,
        align: "center",
        wordWrap: { width: w - 30 },
      }).setOrigin(0.5, 0);
      this.add(hopeEnh);
    }

    const divider = cfg.scene.add.rectangle(0, bodyMid, w - 28, 1, THEME.borderSubtle, 0.5);
    this.add(divider);

    const fearLabel = cfg.scene.add.text(0, bodyMid + 5, "СТРАХ", {
      ...TEXT_STYLE.small, fontSize: "9px", fontStyle: "bold", color: "#c9889a",
    }).setOrigin(0.5, 0);
    fearLabel.setLetterSpacing(2);
    this.add(fearLabel);

    const fearFormula = this.renderFormulaText(cfg.scene, card.fear.effects, w, cfg.stats);
    fearFormula.setPosition(0, bodyMid + 21);
    fearFormula.setOrigin(0.5, 0);
    this.add(fearFormula);

    if (fearEnhText) {
      const fearEnh = cfg.scene.add.text(0, bodyMid + 21 + fearFormula.height + 2, fearEnhText, {
        ...TEXT_STYLE.small,
        fontSize: "8.5px",
        fontStyle: "bold",
        color: enhColor,
        align: "center",
        wordWrap: { width: w - 30 },
      }).setOrigin(0.5, 0);
      this.add(fearEnh);
    }

    if (cfg.count && cfg.count > 1) {
      const countBg = rex.add.roundRectangle(w / 2 - 14, -h / 2 + 14, 26, 18, 4, THEME.bgElevated, 1)
        .setStrokeStyle(1, THEME.borderAccent, 0.7);
      this.add(countBg as unknown as Phaser.GameObjects.GameObject);
      const countText = cfg.scene.add.text(w / 2 - 14, -h / 2 + 14, `×${cfg.count}`, {
        ...TEXT_STYLE.display,
        fontSize: "11px",
        fontStyle: "bold",
        color: "#c8a04a",
      }).setOrigin(0.5);
      this.add(countText);
    }

    this.canHope = canHope;
    this.canFear = canFear;
    this.onPlayHope = cfg.onPlayHope;
    this.onPlayFear = cfg.onPlayFear;

    this.headerEndY = bodyTop + h / 2;
    this.zoneSplitY = bodyMid + h / 2;
    this.zoneBottomY = bodyBottom + h / 2;

    if (playable) {
      this.hopeZone = cfg.scene.add
        .rectangle(0, (bodyTop + bodyMid) / 2, w - 10, bodyMid - bodyTop, THEME.hopeMuted, 0)
        .setOrigin(0.5);
      this.fearZone = cfg.scene.add
        .rectangle(0, (bodyMid + bodyBottom) / 2, w - 10, bodyBottom - bodyMid, THEME.fearMuted, 0)
        .setOrigin(0.5);
      this.addAt(this.hopeZone, 2);
      this.addAt(this.fearZone, 3);
    }

    const costStr = card.hope.cost === card.fear.cost ? `${card.hope.cost}` : `${card.hope.cost} / ${card.fear.cost}`;
    const costRow = cfg.scene.add.text(0, costY, `СТОИМОСТЬ ${costStr}`, {
      ...TEXT_STYLE.small, fontSize: "10px", fontStyle: "bold",
      color: playable || !interactive ? "#c8a04a" : "#5a5460",
    }).setOrigin(0.5);
    costRow.setLetterSpacing(1.5);
    this.add(costRow);

    this.setSize(w, h);
    if (playable) {
      this.setInteractive({ useHandCursor: true });
      this.on("pointerover", () => this.onHover());
      this.on("pointerout", () => this.onUnhover());
      this.on("pointermove", (_p: Phaser.Input.Pointer, lx: number, ly: number) => this.updateZoneHighlight(lx, ly));
      this.on("pointerdown", (_p: Phaser.Input.Pointer, lx: number, ly: number, ev: { stopPropagation?: () => void }) => {
        const zone = this.detectZone(lx, ly);
        if (zone === "hope" && this.canHope) this.onPlayHope?.();
        else if (zone === "fear" && this.canFear) this.onPlayFear?.();
        ev?.stopPropagation?.();
      });
    }
  }

  private detectZone(localX: number, localY: number): "hope" | "fear" | null {
    if (localX < 6 || localX > this.cardWidth - 6) return null;
    if (localY < this.headerEndY || localY > this.zoneBottomY) return null;
    return localY < this.zoneSplitY ? "hope" : "fear";
  }

  private updateZoneHighlight(localX: number, localY: number) {
    const zone = this.detectZone(localX, localY);
    this.hopeZone?.setFillStyle(THEME.hopeMuted, zone === "hope" && this.canHope ? 0.2 : 0);
    this.fearZone?.setFillStyle(THEME.fearMuted, zone === "fear" && this.canFear ? 0.2 : 0);
  }

  setBaseY(y: number) {
    this.baseY = y;
    this.y = y;
  }

  setBaseDepth(d: number) {
    this.baseDepth = d;
    this.setDepth(d);
  }

  private onHover() {
    if (this.isHovering) return;
    if (this.parentContainer) {
      for (const sib of this.parentContainer.list) {
        if (sib === this) continue;
        (sib as BattleCard).snapUnhover();
      }
    }
    this.isHovering = true;
    this.setDepth(1000);
    this.sortHandLayer();
    this.scene.tweens.add({
      targets: this,
      y: this.baseY - 60,
      scale: 1.22,
      duration: 220,
      ease: "Back.easeOut",
    });
    this.bg.setStrokeStyle(3, THEME.leafGlow, 1);
    if (this.glowRing) {
      this.scene.tweens.add({
        targets: this.glowRing,
        alpha: 0.55,
        duration: 220,
        ease: "Back.easeOut",
      });
    }
  }

  snapUnhover() {
    if (!this.isHovering && this.y === this.baseY && this.scale === 1) return;
    this.isHovering = false;
    this.setDepth(this.baseDepth);
    this.scene.tweens.killTweensOf(this);
    if (this.glowRing) this.scene.tweens.killTweensOf(this.glowRing);
    this.bg.setStrokeStyle(3, THEME.borderAccent, 0.9);
    this.scene.tweens.add({
      targets: this,
      y: this.baseY,
      scale: 1,
      duration: 140,
      ease: "Sine.easeOut",
    });
    if (this.glowRing) {
      this.scene.tweens.add({
        targets: this.glowRing,
        alpha: 0,
        duration: 120,
        ease: "Sine.easeOut",
      });
    }
    this.sortHandLayer();
  }

  private sortHandLayer() {
    const scene = this.scene as Phaser.Scene & { sortHand?: () => void };
    if (typeof scene.sortHand === "function") scene.sortHand();
  }

  private onUnhover() {
    if (!this.isHovering) return;
    this.isHovering = false;
    this.setDepth(this.baseDepth);
    this.scene.tweens.add({
      targets: this,
      y: this.baseY,
      scale: 1,
      duration: 220,
      ease: "Sine.easeOut",
    });
    this.bg.setStrokeStyle(3, THEME.borderAccent, 0.9);
    if (this.glowRing) {
      this.scene.tweens.add({
        targets: this.glowRing,
        alpha: 0,
        duration: 180,
      });
    }
    this.hopeZone?.setFillStyle(THEME.hopeMuted, 0);
    this.fearZone?.setFillStyle(THEME.fearMuted, 0);
  }

  private renderFormulaText(scene: Phaser.Scene, effects: CardEffect[], cardW: number, stats?: Stats): Phaser.GameObjects.Text {
    const segments: string[] = [];
    let i = 0;
    while (i < effects.length) {
      const e = effects[i];
      if (e.type === "bonusAttack") {
        let count = 1;
        while (i + count < effects.length) {
          const n = effects[i + count];
          if (n.type === "bonusAttack" && n.scope === e.scope && n.bonusFromStat === e.bonusFromStat) count++;
          else break;
        }
        const scope = e.scope === "nextAttack" ? "След. атака" : "Все атаки";
        const stat = STAT_SHORT[e.bonusFromStat];
        const mult = count > 1 ? `${count}×${stat}` : stat;
        segments.push(`${scope}: +${mult}`);
        i += count;
      } else {
        segments.push(this.effectToString(e, stats));
        i++;
      }
    }
    return scene.add.text(0, 0, segments.join("\n"), {
      ...TEXT_STYLE.body,
      fontSize: "11px",
      align: "center",
      wordWrap: { width: cardW - 16 },
      lineSpacing: 3,
      color: "#e8e4d6",
    });
  }

  private effectToString(e: CardEffect, stats?: Stats): string {
    switch (e.type) {
      case "damage": {
        const main = `Урон ${this.formulaText(e.base, e.dice, e.statScale, e.levelScale)}${this.rangeSuffix(e.base, e.dice, e.statScale, e.levelScale, stats)}`;
        if (e.hpBelow) {
          const ref = e.hpBelow.ofSelf ? "ваше HP" : "HP врага";
          const altDice = e.hpBelow.replaceDice ? `${e.hpBelow.replaceDice.count}d${e.hpBelow.replaceDice.sides}` : "";
          return `${main}\nЕсли ${ref} <${e.hpBelow.pct}%: ${altDice}`;
        }
        return main;
      }
      case "heal":     return `Лечение ${this.formulaText(e.base, e.dice, e.statScale, e.levelScale)}${this.rangeSuffix(e.base, e.dice, e.statScale, e.levelScale, stats)}`;
      case "draw":     return `+${e.count} карт`;
      case "bonusDraw":return `При крите: +${e.count} карт`;
      case "bonusAttack": {
        const scope = e.scope === "nextAttack" ? "След. атака" : "Все атаки";
        return `${scope}: +${STAT_SHORT[e.bonusFromStat]}`;
      }
      case "armorDebuff": {
        if (e.value < 0) {
          return e.onlyOnCrit ? `При крите: +${-e.value} армор` : `+${-e.value} армор`;
        }
        return e.onlyOnCrit ? `При крите: −${e.value} армор цели` : `−${e.value} армор цели`;
      }
      case "armorBuff": return `+${this.formulaText(e.base, e.dice, e.statScale)} армора${this.rangeSuffix(e.base, e.dice, e.statScale, undefined, stats)}`;
      case "selfDamage": return `Себе: −${this.formulaText(e.base, e.dice, e.statScale)} HP${this.rangeSuffix(e.base, e.dice, e.statScale, undefined, stats)}`;
      case "applyStatus": return describeApplyStatus(e);
    }
  }

  private rangeSuffix(base: number, dice?: DiceNotation, scale?: { stat: Stat; mult: number }, levelScale?: { mult: number }, stats?: Stats): string {
    if (!stats) return "";
    let min = base, max = base;
    if (dice) { min += dice.count; max += dice.count * dice.sides; }
    if (scale) { const flat = statMod(stats[scale.stat] ?? 0) * scale.mult; min += flat; max += flat; }
    if (levelScale && this.ownerLevel != null) { const flat = this.ownerLevel * levelScale.mult; min += flat; max += flat; }
    return ` (${formatRange({ min, max })})`;
  }

  private formulaText(base: number, dice?: DiceNotation, scale?: { stat: Stat; mult: number }, levelScale?: { mult: number }): string {
    const parts: string[] = [];
    if (base > 0) parts.push(String(base));
    if (dice) parts.push(`${dice.count}d${dice.sides}`);
    if (scale) {
      parts.push(scale.mult > 1 ? `${scale.mult}×${STAT_SHORT[scale.stat]}` : STAT_SHORT[scale.stat]);
    }
    if (levelScale) {
      parts.push(levelScale.mult > 1 ? `${levelScale.mult}×Ур` : "Ур");
    }
    return parts.join(" + ");
  }
}
